import styled from "styled-components";
import { tokens } from "../../../public/tokens";
import { MOBILE_BREAKPOINT } from "../../library/breakpoints";
import FlexColumn from "../../library/FlexColumn";

const spacing5 = tokens.globals["spacing-5"]; // 20px
const spacing6 = tokens.globals["spacing-6"]; // 24px

const fourColumnGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    columnGap: spacing5,
    rowGap: spacing5,
    width: "100%",
    alignItems: "start",
};

/*
 * No marginTop here — the tab bar (`AdvancedTabs`) already supplies the 24px gap
 * above this wrapper via its own marginBottom (the same single source of truth the
 * Programmes list view relies on), so an extra marginTop here only risks doubling
 * that gap wherever it doesn't collapse into the tab bar's margin.
 */
/*
 * No paddingBottom here — Programs.js applies the "bottom-100" class to the outer
 * .sidebar-right scroll container whenever this form (and its sticky footer) is shown,
 * the same single source of truth Career Library's CareerOpenWrapper uses. Reserving
 * bottom space here too would double up the gap above the sticky footer.
 */
export const ProgrammeContentWrapper = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: spacing6,
    width: "100%",
});

export const PageSections = FlexColumn;

export const UnsavedChangesText = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1"],
    color: tokens.colors.text.secondary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    whiteSpace: "nowrap",

    i: {
        color: tokens.colors.states.warning["500"],
        fontSize: "12px",
    },
});

export const ProgrammeSection = styled.section({
    width: "100%",
    background: tokens.colors.surfaces["container-background"],
    border: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
    overflow: "hidden",
    boxShadow: tokens.effects.effects.shadow.default,
});

export const ProgrammeSectionHeader = styled.div({
    width: "100%",
    padding: `${spacing6} ${spacing6} 0`,
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
});

export const ProgrammeSectionTitle = styled.h2({
    margin: 0,
    color: tokens.colors.text.primary,
    ...tokens.typography.Heading["heading-3"],
});

export const ProgrammeSectionBody = styled.div({
    width: "100%",
    padding: spacing6,
});

export const ProgrammeInformationGrid = styled.div({
    ...fourColumnGrid,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        gridTemplateColumns: "1fr",
        rowGap: spacing5,
    },
});

export const ProgrammeField = styled.div({
    minWidth: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-2"],
});

export const IntegrationStack = styled(FlexColumn)({
    gap: spacing5,
});

export const ReRegistrationFrequencyField = styled.div({
    gridColumn: 1,
    gridRow: 1,
});

export const ReRegistrationCountField = styled.div({
    gridRow: 2,
});

export const ReRegistrationGrid = styled.div({
    ...fourColumnGrid,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        gridTemplateColumns: "1fr",
    },
});

export const SectionLoadingIcon = styled.i({
    marginLeft: tokens.globals["spacing-2"],
});


export const AccordionWrapper = styled(FlexColumn)({
    gap: spacing5,
});

/*
 * One flex row per section: the card (header + collapsible body, sharing a
 * single border) grows to fill the width, and the toggle button - sized to
 * just the header row - stays pinned to its top via alignItems, exactly like
 * the Figma "Dashboard Bar" frame (card flex-1, button a fixed-size sibling).
 */
export const AccordionSection = styled.div({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    width: "100%",
    gap: tokens.globals["spacing-md"],
});

/*
 * The header and the collapsible body live inside this single bordered card
 * so there's one continuous outline, not two boxes stacked with a seam
 * between them.
 */
export const AccordionCard = styled.div({
    flex: "1 1 auto",
    minWidth: 0,
    boxSizing: "border-box",
    background: tokens.colors.surfaces["container-background"],
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
    overflow: "hidden",
    border: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
});

export const AccordionCardHeader = styled.div({
    height: "52px",
    boxSizing: "border-box",
    padding: tokens.globals["spacing-md"],
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
});

export const AccordionButton = styled.button({
    width: "54px",
    height: "54px",
    flex: "0 0 54px",
    padding: 0,
    border: 0,
    borderRadius: tokens.globals["border-radius"],
    background: tokens.colors.theme["topbar-color"],
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0px 1.5px 2px 0px rgba(0, 0, 0, 0.30)",
    cursor: "pointer",
    transition: "opacity 0.15s ease-in-out",

    "&:hover": {
        opacity: 0.85,
    },

    '&[aria-expanded="true"] .rotate-icon': {
        transform: "rotate(180deg)",
    },
});

export const AccordionTitle = styled.span({
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["base-16px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",
});

export const AccordionChevronIcon = styled.i({
    color: tokens.colors.neutral.white,
    fontSize: "30px",
    lineHeight: 1,
    transition: "transform 0.3s ease-in-out",
});

export const AccordionContent = styled.div({
    width: "100%",

    "&.collapse:not(.show)": {
        display: "none",
    },

    "&.show": {
        display: "block",
    },
});

export const AccordionBody = styled.div({
    padding: `0 ${tokens.globals["spacing-md"]} ${tokens.globals["spacing-md"]}`,
});

export const CheckboxRow = styled.div({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: spacing5,
});

export const AccordionStack = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-md"],
});
